import { blockInterruptedPost } from './ownership';
import { classifyQueueFailure, interruptedPostFailure, queueFailureChanges } from './preparationPolicy';
import { sendQueueRecord } from './sender';
import { hasPostStarted, validateQueueRecordInput } from './storage';
import { listQueuePreparationRecords } from './sessionStorage';

function canPrepare(record, getCaptchaToken, now) {
  try {
    validateQueueRecordInput(record);
  } catch {
    return false;
  }

  return (
    record.schemaVersion === 1 &&
    [record.ownership?.expiresAt, record.sessionOwnershipExpiresAt].every(
      (time) => time == null || (Number.isFinite(time) && time >= 0),
    ) &&
    ['pending', 'preparing'].includes(record.status) &&
    !(record.postStartedRevision != null && record.postStartedRevision >= record.revision) &&
    // Another worker's lease is left alone until a later trigger finds it expired.
    !(record.ownership?.expiresAt > now || record.sessionOwnershipExpiresAt > now) &&
    (!record.captcha.required || typeof getCaptchaToken === 'function')
  );
}

// A started POST left sending without a live lease lost its sender mid-request.
function isInterruptedPost(record, now) {
  return (
    record.status === 'sending' &&
    hasPostStarted(record) &&
    !(record.ownership?.expiresAt > now || record.sessionOwnershipExpiresAt > now)
  );
}

/** Finals whose POST has not started; POST failures are reported as outcomes instead. */
function countFinals(records) {
  const finals = records.filter((record) => record.kind === 'final' && !hasPostStarted(record));

  return {
    pendingCount: finals.filter((record) => record.status !== 'blocked').length,
    blockedCount: finals.filter((record) => record.status === 'blocked').length,
  };
}

// A caller's callback can never change a queue decision.
function notify(callback, value) {
  try {
    callback?.(value);
  } catch {
    // Reporting is best effort.
  }
}

// A started POST cannot be retried; record why the sender stopped reporting.
async function recordInterruptedPost(record) {
  const lastError = interruptedPostFailure();

  try {
    const blocked = await blockInterruptedPost(record, lastError);
    if (!blocked) return undefined;

    return {
      recordId: record.id,
      status: 'failed',
      failure: { ...lastError, status: 'blocked', attempts: blocked.attempts ?? null },
    };
  } catch {
    // Storage is unavailable; a later trigger can record it.
    return undefined;
  }
}

async function sendRecord(record, { apiUrl, signal, getCaptchaToken }) {
  let failure;

  try {
    const sent = await sendQueueRecord(record.id, {
      apiUrl,
      signal,
      getCaptchaToken,
      onFailure: async ({ error, postStarted, update }) => {
        const changes = queueFailureChanges(error, { postStarted });
        const updated = await update(changes);

        // A lost lease or newer revision writes nothing; only a persisted status is reported.
        if (updated) {
          failure = {
            ...classifyQueueFailure(error, postStarted),
            status: changes.status,
            attempts: updated.attempts ?? null,
          };
        }
      },
    });
    const result = { recordId: record.id, status: sent.status };

    if (sent.status === 'submitted') result.feedbackId = sent.feedbackId;

    return result;
  } catch {
    // A corrupt record, storage failure or terminal POST cannot stop its peers.
    return { recordId: record.id, status: 'failed', failure };
  }
}

/**
 * One pass over this environment, started by an open, online or visibility
 * trigger. Each record is tried at most once. It reports persisted final outcomes
 * and queue summaries to its caller, but uses no Flow state, UI effects or transports.
 */
export async function processFeedbackQueue({ apiUrl, signal, getCaptchaToken, onResult, onSummary } = {}) {
  const results = [];

  if (signal?.aborted || globalThis.navigator?.onLine === false) return { results };

  const records = await listQueuePreparationRecords(apiUrl);
  let summary = { ...countFinals(records), inProgress: false };

  notify(onSummary, summary);

  for (const record of records) {
    if (signal?.aborted || globalThis.navigator?.onLine === false) break;

    let result;

    if (isInterruptedPost(record, Date.now())) {
      result = await recordInterruptedPost(record);
    } else if (canPrepare(record, getCaptchaToken, Date.now())) {
      if (record.kind === 'final' && !summary.inProgress) {
        summary = { ...summary, inProgress: true };
        notify(onSummary, summary);
      }

      result = await sendRecord(record, { apiUrl, signal, getCaptchaToken });
    }

    if (!result) continue;

    Object.assign(result, {
      kind: record.kind,
      nodeId: record.nodeId,
      flowId: record.flowId,
      submissionId: record.submissionId,
    });
    results.push(result);
    notify(onResult, result);
  }

  if (!signal?.aborted) {
    notify(onSummary, { ...countFinals(await listQueuePreparationRecords(apiUrl)), inProgress: false });
  }

  return { results };
}
